import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Paper, ActionIcon, Group, Text, Avatar, CloseButton, Indicator } from '@mantine/core';
import { IconMessageCircle, IconX } from '@tabler/icons-react';
import SupportApp from './SupportApp';
import { cfg } from './session';

/**
 * Floating chat-bubble that hangs in a corner of the page.
 * Rendered into a portal directly on document.body so themes
 * with their own stacking context (Enfold, Divi) can't bury it.
 */
export default function SupportBubble({ position = 'br' }) {
    const [open, setOpen] = useState(false);
    const [agent, setAgent] = useState(null);
    const [loadingAgent, setLoadingAgent] = useState(true);
    const [portalHost, setPortalHost] = useState(null);

    useEffect(() => {
        // Create or reuse a host node directly on <body> so the bubble
        // escapes any theme stacking context.
        let host = document.getElementById('ast-bubble-portal');
        if (!host) {
            host = document.createElement('div');
            host.id = 'ast-bubble-portal';
            // Inline guard — extreme z-index, fixed position, full-viewport-anchored.
            host.style.cssText = `
                position: fixed !important;
                inset: 0 !important;
                pointer-events: none !important;
                z-index: 2147483000 !important;
                width: 0; height: 0; overflow: visible;
            `;
            document.body.appendChild(host);
        }
        setPortalHost(host);

        (async () => {
            try {
                const res = await fetch(`${cfg.apiUrl}/support/active-agent/anon-bubble-default`);
                if (res.ok) {
                    const data = await res.json();
                    if (data.agent) setAgent(data.agent);
                }
            } finally {
                setLoadingAgent(false);
            }
        })();
    }, []);

    if (!portalHost) return null;

    const corner = positionToStyle(position);

    const content = (
        <div style={{
            position: 'fixed',
            ...corner,
            pointerEvents: 'none',
        }}>
            {!open && (
                <ActionIcon
                    onClick={() => setOpen(true)}
                    radius="xl"
                    size={64}
                    color={agent?.avatar_color || 'red'}
                    variant="filled"
                    style={{ pointerEvents: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}
                    title={`Chat met ${agent?.name || (cfg.companyName || 'support')}`}
                >
                    <Indicator color="green" size={12} offset={6} withBorder
                               processing={!!agent?.is_online_now}>
                        {agent?.avatar_emoji
                            ? <span style={{ fontSize: 30 }}>{agent.avatar_emoji}</span>
                            : <IconMessageCircle size={28} />}
                    </Indicator>
                </ActionIcon>
            )}

            {open && (
                <Paper
                    withBorder
                    shadow="xl"
                    radius="md"
                    style={{
                        pointerEvents: 'auto',
                        width: 'min(420px, calc(100vw - 32px))',
                        height: 'min(640px, calc(100vh - 32px))',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <Group
                        justify="space-between"
                        p="xs"
                        style={{
                            borderBottom: '1px solid var(--mantine-color-default-border)',
                            background: 'var(--mantine-color-default-hover)',
                        }}
                    >
                        <Group gap={8}>
                            <Avatar color={agent?.avatar_color || 'red'} radius="xl" size="sm">
                                {agent?.avatar_emoji
                                    ? <span style={{ fontSize: 16 }}>{agent.avatar_emoji}</span>
                                    : <IconMessageCircle size={14} />}
                            </Avatar>
                            <div>
                                <Text size="sm" fw={600}>
                                    {loadingAgent
                                        ? 'Verbinden...'
                                        : (agent?.name || `${cfg.companyName || 'Support'}`)}
                                </Text>
                                {agent && (
                                    <Text size="xs" c={agent.is_online_now ? 'green' : 'dimmed'}>
                                        {agent.is_online_now ? '· online' : '· offline'}
                                    </Text>
                                )}
                            </div>
                        </Group>
                        <CloseButton onClick={() => setOpen(false)} aria-label="Sluiten">
                            <IconX size={16} />
                        </CloseButton>
                    </Group>

                    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                        <SupportApp embedded />
                    </div>
                </Paper>
            )}
        </div>
    );

    return createPortal(content, portalHost);
}

function positionToStyle(position) {
    switch (position) {
        case 'bl': return { left: 24, bottom: 24 };
        case 'tl': return { left: 24, top: 24 };
        case 'tr': return { right: 24, top: 24 };
        case 'br':
        default:   return { right: 24, bottom: 24 };
    }
}
